import { ControlPanelConfig, ControlSetRow, t } from '@superset-ui/core';
import { FilterIndex } from './types';
import { DEFAULT_TIME_RANGE } from 'src/explore/constants';

// -------- helpers for default dropdown (SelectAsyncControl) --------
function scalar(v: any) {
  if (Array.isArray(v)) return v[0];
  if (v && typeof v === 'object') return v.value ?? v.label ?? v.name ?? '';
  return v;
}

function epochToISODate(epoch: number): string {
  const ms = epoch > 1e11 ? epoch : epoch * 1000;
  const d = new Date(ms);
  const yyyy = d.getUTCFullYear();
  const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(d.getUTCDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

function looksLikeDateString(s: string) {
  return (
    /^[A-Za-z]{3},\s\d{2}\s[A-Za-z]{3}\s\d{4}/.test(s) ||
    /^\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}:\d{2}/.test(s) ||
    /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(s)
  );
}

function normalizeValue(raw: any, isTemporal: boolean): string | null {
  const v = scalar(raw);
  if (v === null || v === undefined) return null;

  // number: epoch -> date ONLY for temporal
  if (typeof v === 'number' && Number.isFinite(v)) {
    if (isTemporal && v > 1e9) return epochToISODate(v);
    return String(v);
  }

  const s = String(v).trim();
  if (!s) return null;

  // numeric strings: epoch -> date ONLY for temporal
  if (isTemporal) {
    if (/^\d{13}$/.test(s)) return epochToISODate(Number(s));
    if (/^\d{10}$/.test(s)) return epochToISODate(Number(s));
  }

  // already YYYY-MM-DD...
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);

  // parse dates for temporal columns OR for clearly date-like strings
  if (isTemporal || looksLikeDateString(s)) {
    const ts = Date.parse(s);
    if (!Number.isNaN(ts)) return epochToISODate(ts);
  }

  return s;
}

function smartSort(values: string[]): string[] {
  const allNum = values.length > 0 && values.every(v => Number.isFinite(Number(v)));
  if (allNum) return [...values].sort((a, b) => Number(a) - Number(b));

  const allDate = values.length > 0 && values.every(v => /^\d{4}-\d{2}-\d{2}$/.test(v));
  if (allDate) return [...values].sort((a, b) => Date.parse(a) - Date.parse(b));

  return [...values].sort((a, b) => a.localeCompare(b, 'ru', { numeric: true, sensitivity: 'base' }));
}

function formatLabel(v: string): string {
  if (/^\d{4}-\d{2}-\d{2}$/.test(v)) {
    const dd = v.slice(8, 10);
    if (dd === '01') return v.slice(0, 7); // YYYY-MM
    return v; // YYYY-MM-DD
  }
  return v;
}

function parseDatasourceFromState(state: any): { id?: number; type?: string } {
  const ds = state?.form_data?.datasource ?? state?.controls?.datasource?.value;
  if (typeof ds === 'string' && ds.includes('__')) {
    const [idStr, type] = ds.split('__');
    const id = Number(idStr);
    return { id: Number.isFinite(id) ? id : undefined, type };
  }

  if (state?.datasource?.id) {
    const id = Number(state.datasource.id);
    const type =
      state.datasource.datasource_type ||
      state.datasource.type ||
      state.datasource.datasourceType ||
      'table';
    return { id: Number.isFinite(id) ? id : undefined, type: String(type) };
  }
  return {};
}

function isProbablyTemporalColumn(state: any, col: string): boolean {
  const byName = /(^|_)(dttm|dt|date|time|month|day|year)\b/i.test(col);

  const cols: any[] = state?.datasource?.columns ?? [];
  const meta = cols.find(
    c => (c?.column_name ?? c?.columnName ?? '') === col,
  );

  const type = String(meta?.type ?? meta?.data_type ?? meta?.datatype ?? '');
  const byType = /(date|time|timestamp)/i.test(type);
  const byFlag = Boolean(meta?.is_dttm ?? meta?.isDttm);

  return byFlag || byType || byName;
}

function makeColumnValuesEndpoint(dsType: string, dsId: number, col: string) {
  return (
    `/api/v1/datasource/${encodeURIComponent(dsType)}` +
    `/${encodeURIComponent(String(dsId))}` +
    `/column/${encodeURIComponent(col)}/values/` +
    `?q=(page:0,page_size:5000)`
  );
}

function columnValuesMutatorFactory(isTemporal: boolean) {
  return (response: Record<string, any>) => {
    const json = response ?? {};
    const raw = (json as any)?.result ?? (json as any)?.values ?? json;

    let arr: any[] = [];
    if (Array.isArray(raw)) arr = raw;
    else if (raw && Array.isArray((raw as any).result)) arr = (raw as any).result;
    else if (raw && Array.isArray((raw as any).values)) arr = (raw as any).values;
    else if (raw && Array.isArray((raw as any).data)) arr = (raw as any).data;

    const values = arr
      .map(v => normalizeValue(v, isTemporal))
      .filter((v): v is string => Boolean(v));

    const uniq = Array.from(new Set(values));
    const sorted = smartSort(uniq);

    return sorted.map(v => ({ value: v, label: formatLabel(v) }));
  };
}

// ------------------- base controls -------------------
const typeChoices = [
  ['disabled', 'Выключено'],
  ['select', 'Значение'],
  ['date_range', 'Диапазон дат'],
];

function filterBlock(i: FilterIndex): ControlSetRow[] {
  return [
    [
      {
        name: `f${i}_label`,
        config: {
          type: 'TextControl',
          label: 'Метка',
          default: '',
          description: 'Название над селектором',
        },
      },
      {
        name: `f${i}_type`,
        config: {
          type: 'SelectControl',
          label: 'Тип',
          default: 'disabled',
          choices: typeChoices,
          clearable: false,
          renderTrigger: true,
        },
      },
    ],
    [
      {
        name: `f${i}_column`,
        config: {
          type: 'SelectControl',
          label: 'Столбец',
          default: '',
          description: 'Столбец для типа "Значение"',
          mapStateToProps: state => ({
            choices: (state?.datasource?.columns ?? []).map((c: any) => [
              c.column_name,
              c.column_name,
            ]),
          }),
          visibility: ({ controls }) =>
            (controls?.[`f${i}_type`]?.value ?? 'disabled') === 'select',
          renderTrigger: true,
        },
      },
      {
        name: `f${i}_multi`,
        config: {
          type: 'CheckboxControl',
          label: 'Multi-select',
          default: false,
          visibility: ({ controls }) =>
            (controls?.[`f${i}_type`]?.value ?? 'disabled') === 'select',
          renderTrigger: true,
        },
      },
    ],
        // ---- DEFAULT DATE RANGE (DateFilterControl) ----
    [
      {
        name: `f${i}_default_time_range`,
        config: {
          type: 'DateFilterControl',
          label: 'Диапазон дат по умолчанию',
          default: DEFAULT_TIME_RANGE,
          description:
            'Будет выбран при первом открытии дашборда/графика. "No filter" = без фильтра.',
          renderTrigger: true,
          visibility: ({ controls }) =>
            (controls?.[`f${i}_type`]?.value ?? 'disabled') === 'date_range',
        },
      },
    ],

    // ---- DEFAULT VALUE (variant 2): dropdown with DISTINCT via SelectAsyncControl ----
    [
      {
        name: `f${i}_default`,
        config: {
          type: 'SelectAsyncControl',
          label: 'Значение по умолчанию',
          default: undefined,
          description:
            'Какие значения будут выбраны при первом открытии дашборда/графика. Можно очистить.',
          renderTrigger: true,

          visibility: ({ controls }) =>
            (controls?.[`f${i}_type`]?.value ?? 'disabled') === 'select' &&
            Boolean(controls?.[`f${i}_column`]?.value),

          mapStateToProps: (state: any) => {
            const col = String(state?.controls?.[`f${i}_column`]?.value ?? '').trim();
            const multi = Boolean(state?.controls?.[`f${i}_multi`]?.value);

            const { id, type } = parseDatasourceFromState(state);
            const dsType = String(type ?? 'table');

            const isTemporal = col ? isProbablyTemporalColumn(state, col) : false;

            // если чего-то не хватает — отрисуемся, но без запроса
            if (!id || !col) {
              return {
                multi,
                disabled: true,
                placeholder: multi ? 'Выберите значения' : 'Выберите значение',
                dataEndpoint: '/api/v1/chart/_noop', // безопасный "мусорный" endpoint; контрол скрыт visibility если нет col
                mutator: () => [],
              };
            }

            return {
              multi,
              placeholder: multi ? 'Выберите значения' : 'Выберите значение',
              dataEndpoint: makeColumnValuesEndpoint(dsType, id, col),
              mutator: columnValuesMutatorFactory(isTemporal),
            };
          },
        },
      },
    ],
  ];
}

function filterSection(i: FilterIndex) {
  return {
    label: t(`Filter ${i}`),
    expanded: false,
    controlSetRows: filterBlock(i),
  };
}

const controlPanel: ControlPanelConfig = {
  controlPanelSections: [
    {
      label: 'Селекторы',
      expanded: true,
      controlSetRows: [
        [
          {
            name: 'emit_cross_filters',
            config: {
              type: 'CheckboxControl',
              label: 'Enable cross-filtering',
              default: true,
              renderTrigger: true,
              description:
                'Эмитить фильтры в другие графики (ограничивается через Cross-filtering scoping на дашборде).',
            },
          },
        ],
        [
          {
            name: 'cascade_within_chart',
            config: {
              type: 'CheckboxControl',
              label: 'Каскадные значения внутри графика',
              default: false,
              renderTrigger: true,
              description:
                'Если включено — значения в выпадашках зависят от выбранного в других селекторах этого же графика.',
            },
          },
        ],
      ],
    },

    filterSection(1),
    filterSection(2),
    filterSection(3),
    filterSection(4),
    filterSection(5),
    filterSection(6),
    filterSection(7),
    filterSection(8),
    filterSection(9),
    filterSection(10),
  ],
};

export default controlPanel;

